/**
 * AUTOMATIKUS SZÜNET — ugyanaz a szünet, mint a gombé, csak a rendszer
 * „nyomja meg” (Geri döntése, GRUNDO #72; spec: docs/02 „Aktív rögzítés”).
 *
 * TISZTA FÜGGVÉNYEK, időbélyegekkel — nem a faliórával. Ettől működik a
 * háttérből utólag beolvasott (drain) mintákon is ugyanúgy, mint élőben.
 *
 * ⚠️ MÉRT KORLÁT (iOS, `BackgroundLocationPlugin.swift`): normál rögzítésnél a
 * natív `distanceFilter` álló helyzetben EGYETLEN mintát sem enged át. Az
 * állást ezért a CSEND is jelzi: ha a típus `AUTO_PAUSE_ENTER_MS` ideje óta nem jött új pont,
 * nem mozdultunk el a szűrő távolságánál többet. Előtérben ezt a másodperces
 * `autoPauseTick` veszi észre; háttérből visszatérve a következő minta
 * (`applySampleWithAutoPause`) utólag.
 *
 * A GPS-zaj ellen: a `GPS_STATIONARY_RADIUS_M`-en belüli elmozdulás nem mozgás
 * — ugyanaz a sugár, amivel a rögzítő a távot számolja.
 *
 * Ha egy „szünet” alatt a megtett út gyors mozgást jelent (alagút, GPS-kiesés
 * menet közben), a szünetet visszamenőleg töröljük: nem vehet el mozgásidőt.
 */

import { gridDistance, latLngToCell } from 'h3-js';
import { GAMEPLAY } from '@/config/gameplay';
import { distanceM } from '@/game/geo';
import type { TracePoint } from '@/types';
import { applySample, resume, type RecorderState } from './recorder';
import type { PositionSample } from './types';

/** Ennyi figyelt mintát őrzünk szünet alatt — a kilépéshez bőven elég. */
const MAX_WATCHED_SAMPLES = 120;

interface Stamped {
  lat: number;
  lng: number;
  t: number;
}

function withinRadius(a: Stamped, b: Stamped): boolean {
  const res = GAMEPLAY.H3_RESOLUTION;
  try {
    return (
      gridDistance(latLngToCell(a.lat, a.lng, res), latLngToCell(b.lat, b.lng, res)) <=
      GAMEPLAY.AUTO_PAUSE_RADIUS_CELLS
    );
  } catch {
    // A h3 nagyon távoli cellák közt nem tud távolságot mondani — az nem állás.
    return false;
  }
}

/** Mozgás-e `from` → `to`: a zajsugáron túl, és a típus küszöbe fölötti átlaggal. */
function isMoving(type: RecorderState['type'], from: Stamped, to: Stamped): boolean {
  const seconds = (to.t - from.t) / 1000;
  if (seconds <= 0) return false;
  const moved = distanceM(from, to);
  if (moved <= GAMEPLAY.GPS_STATIONARY_RADIUS_M) return false;
  return (moved / seconds) * 3.6 >= GAMEPLAY.AUTO_PAUSE_SPEED_KMH[type];
}

function speedKmh(from: Stamped, to: Stamped): number {
  const seconds = (to.t - from.t) / 1000;
  return seconds > 0 ? (distanceM(from, to) / seconds) * 3.6 : 0;
}

/**
 * A belépési ablak típusonként: bringa 15 s, futás 30 s, séta 60 s (Geri,
 * #73). Rövidebb ablakban a küszöb nem mérhető (1 km/h 10 s alatt 2,8 m, a
 * GPS-zaj 5–15 m); a konfigurált értékek ennél mind hosszabbak.
 */
export function enterWindowMs(type: RecorderState['type']): number {
  return GAMEPLAY.AUTO_PAUSE_ENTER_MS[type];
}

/** Az utolsó folytatás (vagy a rajt) előtti pont nem számít — különben egy
 *  automatikus folytatás után azonnal visszaesnénk szünetbe. */
function armedAt(state: RecorderState): number {
  return state.resumedAt ?? state.startedAt ?? 0;
}

/**
 * CSEND: `now`-ig legalább `enterWindowMs` óta nem jött pont → a natív szűrő
 * szerint nem mozdultunk el. A megállás helye és ideje az utolsó pont.
 */
function silentSince(state: RecorderState, now: number): TracePoint | null {
  if (state.status !== 'recording') return null;
  const last = state.points[state.points.length - 1];
  if (last === undefined || last.t < armedAt(state)) return null;
  return now - last.t >= enterWindowMs(state.type) ? last : null;
}

/**
 * MINTÁS ÁLLÁS: az utolsó pontig visszamenőleg legalább `enterWindowMs`
 * hosszan, végig megfigyelt pontokkal, a ±2 cellán belül és a küszöb alatt
 * (GPS-zaj álló helyzetben). A megállás helye az ablak eleji pont.
 */
export function stationarySince(state: RecorderState): TracePoint | null {
  if (state.status !== 'recording') return null;
  const points = state.points;
  const last = points[points.length - 1];
  if (last === undefined) return null;

  const windowStart = last.t - enterWindowMs(state.type);
  let baseIndex = -1;
  for (let i = points.length - 1; i >= 0; i -= 1) {
    if (points[i]!.t <= windowStart) {
      baseIndex = i;
      break;
    }
  }
  if (baseIndex < 0) return null;
  const base = points[baseIndex]!;
  if (base.t < armedAt(state)) return null;

  for (let i = baseIndex + 1; i < points.length; i += 1) {
    if (!withinRadius(base, points[i]!)) return null;
  }
  return isMoving(state.type, base, last) ? null : base;
}

function enterAutoPause(state: RecorderState, anchor: TracePoint, watch: PositionSample[] = []): RecorderState {
  return {
    ...state,
    status: 'paused',
    pausedAt: anchor.t,
    pauseKind: 'auto',
    autoPause: { anchor, watch },
  };
}

/** Folytatás `at`-kor, majd a megadott (időrendi) minták rögzítése. */
function resumeAndApply(state: RecorderState, at: number, samples: readonly PositionSample[]): RecorderState {
  return samples.reduce(applySample, resume(state, at));
}

/** Másodperces ellenőrzés előtérben: a csend is állás (lásd a fejlécet). */
export function autoPauseTick(state: RecorderState, now: number): RecorderState {
  const anchor = silentSince(state, now);
  return anchor === null ? state : enterAutoPause(state, anchor);
}

/**
 * Egy minta feldolgozása automatikus szünettel. Kézi szünetben és leállás
 * után ugyanaz, mint az `applySample` (a minta elvész).
 */
export function applySampleWithAutoPause(state: RecorderState, sample: PositionSample): RecorderState {
  if (state.status === 'paused' && state.pauseKind === 'auto' && state.autoPause) {
    return applyWhileAutoPaused(state, sample);
  }
  if (state.status !== 'recording') return applySample(state, sample);

  // Csend utáni minta: a csend alatt álltunk-e? (háttérből visszatérve is)
  const stop = silentSince(state, sample.t);
  if (stop !== null) {
    if (withinRadius(stop, sample) && !isMoving(state.type, stop, sample)) {
      return enterAutoPause(state, stop, [sample]);
    }
    // Kiesés menet közben (gyors elmozdulás a csend alatt): nem szünet.
    if (isMoving(state.type, stop, sample)) return applySample(state, sample);
    // Álltunk, és ezzel a mintával indultunk el.
    return resumeAndApply(enterAutoPause(state, stop), sample.t, [sample]);
  }

  const next = applySample(state, sample);
  const anchor = stationarySince(next);
  return anchor === null ? next : enterAutoPause(next, anchor);
}

function applyWhileAutoPaused(state: RecorderState, sample: PositionSample): RecorderState {
  const { anchor, watch } = state.autoPause!;
  if (sample.t <= anchor.t) return state;
  const watched = [...watch, sample].sort((a, b) => a.t - b.t).slice(-MAX_WATCHED_SAMPLES);

  // Az INDULÁS: a zajsugáron túli minták záró sorozatának első eleme. Innen
  // folytatunk, nem onnan, ahol a ±2 cellát elhagytuk — különben az indulás
  // utáni pár másodperc is szünetnek számítana.
  const beyondNoise = (s: Stamped) => distanceM(anchor, s) > GAMEPLAY.GPS_STATIONARY_RADIUS_M;
  let departIndex = watched.length;
  for (let i = watched.length - 1; i >= 0 && beyondNoise(watched[i]!); i -= 1) departIndex = i;
  const depart = watched[departIndex];
  const fromDeparture = (at: number) => resumeAndApply(state, at, watched.filter((s) => s.t >= at));

  if (!withinRadius(anchor, sample)) {
    // A megállás óta ÁTLAGBAN is gyors mozgás, köztes minták nélkül → nem
    // szünet volt, hanem kiesés menet közben: töröljük (0 ms szünet).
    if (isMoving(state.type, anchor, sample)) return fromDeparture(anchor.t);
    return fromDeparture(depart?.t ?? sample.t);
  }

  // A ±2 cellán belül: legalább `AUTO_PAUSE_EXIT_MS` óta a küszöb fölött?
  if (
    depart !== undefined &&
    sample.t - depart.t >= GAMEPLAY.AUTO_PAUSE_EXIT_MS &&
    speedKmh(depart, sample) >= GAMEPLAY.AUTO_PAUSE_SPEED_KMH[state.type]
  ) {
    return fromDeparture(depart.t);
  }
  return { ...state, autoPause: { anchor, watch: watched } };
}
